/**
 * Module dependencies.
 */

import { Box, color, media, units } from '@untile/react-components';
import {
  Button,
  IconButton,
  Image,
  RouterLink,
  icons
} from 'src/components/core';

import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from 'react';
import { ModelRangeProps, ModelRangeSpec } from 'src/types/model-ranges';
import { authRoutes } from 'src/core/routes';
import { ifProp, theme } from 'styled-tools';
import {
  modelRangeSpecs,
  orderedSpecs
} from 'src/core/content-config/model-ranges';

import { useAuth } from 'src/context/auth/context';
import { useFavoritesContext } from 'src/context/favorites/context';
import { useRouter } from 'next/router';
import RemoveFavoriteModal from 'src/components/my-products/favorites/remove-favorite-modal';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import replace from 'lodash/replace';
import styled, { css } from 'styled-components';
import useFavoriteExists from 'src/api/my-products/favorites/use-favorite-exists';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = Pick<
  ModelRangeProps,
  | 'butane'
  | 'diameter'
  | 'height'
  | 'id'
  | 'listThumbnail'
  | 'propane'
  | 'tareWeight'
  | 'waterCapacity'
> & {
  className?: string;
  hasButanePropaneSpecs?: boolean;
  hoveredSpec: ModelRangeSpec | null;
  isLastCard: boolean;
  onHoverSpec: Dispatch<SetStateAction<ModelRangeSpec | null>>;
  url: string;
};

/**
 * `Card` styled component.
 */

const Card = styled(RouterLink)`
  color: ${color('grey800')};
  cursor: pointer;
  display: grid;
  grid-template-rows: ${units(29.5)} 1fr;
  position: relative;
  width: ${units(17)};

  &::before {
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transition: box-shadow ${theme('animations.defaultTransition')};
    z-index: -1;
  }

  &:focus,
  &:hover {
    &::before {
      box-shadow: 0 ${units(1.5)} ${units(4)}
        ${color.transparentize('black', 0.15)};
    }
  }

  ${media.min('ms')`
    grid-template-rows: ${units(45.5)} 1fr;
    width: ${units(26)};
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  margin: ${units(5)} 0;
  overflow: hidden;
  padding-bottom: 144.2%;
  position: relative;
`;

/**
 * `FavoriteButton` styled component.
 */

const FavoriteButton = styled(IconButton)`
  position: absolute;
  right: ${units(2)};
  top: ${units(2)};
`;

/**
 * `DetailsWrapper` styled component.
 */

const DetailsWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

/**
 * `Cell` styled component.
 */

const Cell = styled.div<{
  isHovered: boolean;
  isLastCard: boolean;
}>`
  ${theme('typography.styles.small')}

  border-bottom: 1px solid ${color('grey50')};
  padding: ${units(0.5)} 0;
  position: relative;
  text-align: center;
  width: 100%;

  &:last-of-type {
    margin-bottom: ${units(5)};
  }

  &::before {
    bottom: 0;
    content: '';
    left: -${units(4)};
    position: absolute;
    right: ${ifProp('isLastCard', 0, '-32px')};
    top: 0;
    transition: background-color ${theme('animations.fastTransition')};
    z-index: -1;
  }

  ${ifProp(
    'isHovered',
    css`
      &::before {
        background-color: ${color('grey25')};
      }
    `
  )}

  ${media.min('ms')`
    ${theme('typography.styles.p')}
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  margin: 0 auto ${units(4.5)};
`;

/**
 * `ModelRangeCard` component.
 */

const ModelRangeCard = (props: Props): ReactElement => {
  const {
    className,
    hasButanePropaneSpecs = true,
    hoveredSpec,
    id,
    isLastCard,
    listThumbnail,
    onHoverSpec,
    url
  } = props;

  const router = useRouter();
  const { locale, translate } = useTranslate();
  const { isAuthenticated, token } = useAuth();
  const { isSubmitting, onAddFavorite, onRemoveFavorite } =
    useFavoritesContext();

  const {
    data: existingFavorite,
    isLoading,
    refetch
  } = useFavoriteExists({
    favorableId: id,
    token
  });

  const [isModalOpen, setModalOpen] = useState<boolean>();
  const specifications = useMemo(
    () =>
      reduce(
        orderedSpecs,
        (specifications, spec) => {
          if (
            hasButanePropaneSpecs ||
            (!hasButanePropaneSpecs &&
              spec !== modelRangeSpecs.butane &&
              spec !== modelRangeSpecs.propane)
          ) {
            return {
              ...specifications,
              [spec]: props[spec]
            };
          }

          return specifications;
        },
        {} as Record<ModelRangeSpec, string>
      ),
    [hasButanePropaneSpecs, props]
  );

  const isFavorited = useMemo(() => {
    return !isEmpty(existingFavorite);
  }, [existingFavorite]);

  const handleRemoveFavorite = useCallback(() => {
    onRemoveFavorite(existingFavorite.favorite.id).then(() => {
      setModalOpen(false);
      refetch();
    });
  }, [existingFavorite?.favorite?.id, onRemoveFavorite, refetch]);

  const handleClickFavorite = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();

      if (!isAuthenticated) {
        window.localStorage.setItem('favorite', id);
        router.push(authRoutes.signIn);

        return;
      }

      if (isFavorited) {
        setModalOpen(true);

        return;
      }

      onAddFavorite(id).then(refetch);
    },
    [id, isAuthenticated, isFavorited, onAddFavorite, refetch, router]
  );

  return (
    <Card
      className={className}
      href={url}
      locale={locale}
    >
      <Box position={'relative'}>
        <ImageWrapper>
          <Image
            alt={listThumbnail}
            fill
            src={listThumbnail}
            style={{ objectFit: 'cover' }}
          />
        </ImageWrapper>

        <FavoriteButton
          disabled={isLoading || isSubmitting}
          icon={isFavorited ? icons.heartFull : icons.heart}
          iconSize={units(3)}
          onClick={event => handleClickFavorite(event)}
        />

        <RemoveFavoriteModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          onRemoveFavorite={handleRemoveFavorite}
        />
      </Box>

      <DetailsWrapper>
        {map(Object.keys(specifications), (spec: ModelRangeSpec) => (
          <Cell
            isHovered={hoveredSpec === spec}
            isLastCard={isLastCard}
            key={spec}
            onMouseEnter={() => onHoverSpec(spec)}
            onMouseLeave={() => onHoverSpec(null)}
          >
            {replace(specifications[spec] ?? '—', '.', ',')}
          </Cell>
        ))}

        <StyledButton
          colorTheme={'tertiary'}
          size={'small'}
        >
          {translate('common:actions.moreDetails')}
        </StyledButton>
      </DetailsWrapper>
    </Card>
  );
};

/**
 * Export `ModelRangeCard` component.
 */

export default ModelRangeCard;
